import React, { useState } from "react";
import styled from "styled-components";

import BasicInput from "features/shared/components/form/components/basicInput";
import SelectInput from "features/shared/components/form/components/selectInput";
import SubmitButton from "features/shared/components/form/components/submitButton";
import useSubmit from "features/shared/components/form/hooks/useSubmit";
import LabelWrapper from "features/shared/components/form/components/labelWrapper";

import { ROLE_SELECTION_OPTIONS } from "../constants/form";
import { verifyForm } from "../functions/form";

import { Roles, User } from "../types/access";

interface Props {
    onSubmitCallback: (fields: User) => void;
    submitBtnText: string;
    originalFields?: User;
    readOnlyMode?: boolean;
    initialForm: User;
}

const AccessForm: React.FC<Props> = ({
    onSubmitCallback,
    submitBtnText,
    originalFields,
    readOnlyMode = false,
    initialForm,
}) => {
    const [fields, setFields] = useState<User>(
        originalFields || {
            ...initialForm,
        }
    );
    const [handleSubmit] = useSubmit({
        fields,
        readOnlyMode,
        handleFormVerification: async (fields) => {
            return await verifyForm({
                fields,
                isUpdating: originalFields?.email !== "",
            });
        },
        onSubmitCallback,
    });

    const handleRawChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        handleChange(e.target.name, e.target.value);
    };

    const handleChange = (name: string, value: string) => {
        if (readOnlyMode) return;

        let clonedFields = {
            ...fields,
        };

        switch (name) {
            case "displayName":
                clonedFields.displayName = value;
                break;
            case "email":
                clonedFields.email = value;
                break;
            case "password":
                clonedFields.password = value;
                break;
        }

        setFields(clonedFields);
    };

    const handleSelect = (value: Roles) => {
        setFields({
            ...fields,
            role: value,
        });
    };

    return (
        <StyledForm onSubmit={handleSubmit} autoComplete="off">
            <LabelWrapper label="Display Name">
                <BasicInput
                    name="displayName"
                    value={fields.displayName}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper label="Email">
                <BasicInput
                    name="email"
                    value={fields.email}
                    onChangeHandler={handleRawChange}
                />
            </LabelWrapper>
            <LabelWrapper
                label={(readOnlyMode ? "Selected " : "Add ") + "Role"}
            >
                <SelectInput
                    value={fields.role || Roles.USER}
                    readOnlyMode={readOnlyMode}
                    selectionOptions={ROLE_SELECTION_OPTIONS}
                    onSelectHandler={handleSelect}
                />
            </LabelWrapper>
            {!originalFields && (
                <LabelWrapper label="Password">
                    {!readOnlyMode && (
                        <BasicInput
                            name="password"
                            value={fields.password}
                            onChangeHandler={handleRawChange}
                        />
                    )}
                </LabelWrapper>
            )}
            {!readOnlyMode && <SubmitButton text={submitBtnText} />}
        </StyledForm>
    );
};

const StyledForm = styled.form`
    overflow: auto;
    padding: 5%;
    color: ${(props) => props.theme.colors.neutral[800]};
`;

export default AccessForm;
